import { FullscreenOutlined, LoadingOutlined, ReloadOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons'
import { Alert, Button, Space, Tooltip } from 'antd'
import OpenSeadragon from 'openseadragon'
import type { CSSProperties } from 'react'
import { useEffect, useRef, useState } from 'react'
import { getAssetDeepZoomDerivative, resolveSaasApiUrl } from '../api'
import type { AssetDeepZoomDerivative } from '../api'

/**
 * Storage/Worker Execution Contract (A1) §25/§26/§33 — the ONE OpenSeadragon
 * wrapper both the admin item detail page and the public item detail page
 * use. Storage-neutral by construction: this component never knows whether
 * a given deployment is Local-disk or R2-backed — it only ever calls
 * `GET .../derivatives/deepzoom` and builds tile URLs from the
 * `tiles_base_url` that endpoint returns, exactly like the rest of the
 * frontend already treats Asset variant delivery.
 *
 * Polls while `status` is `pending`/`processing` (the Task hasn't reached a
 * terminal state yet — a real async Job, never assumed synchronous) and
 * always keeps `fallbackImageUrl` (typically the `original`/`preview`
 * variant) visible underneath/instead of the viewer for every non-`ready`
 * state, so the page is never blank (spec §33).
 */
export default function DeepZoomViewer({
  workspaceId,
  assetId,
  fallbackImageUrl,
  alt,
  height = 480,
}: {
  workspaceId: string
  assetId: string
  fallbackImageUrl?: string | null
  alt?: string
  height?: number | string
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const viewerRef = useRef<OpenSeadragon.Viewer | null>(null)
  const [derivative, setDerivative] = useState<AssetDeepZoomDerivative | null>(null)
  const [pollError, setPollError] = useState<string | null>(null)

  // -------------------------------------------------------- Poll for status
  useEffect(() => {
    let cancelled = false
    let timer: number | undefined
    setDerivative(null)
    setPollError(null)

    const poll = () => {
      getAssetDeepZoomDerivative(workspaceId, assetId)
        .then((r) => {
          if (cancelled) return
          setDerivative(r.data)
          setPollError(null)
          if (r.data.status === 'pending' || r.data.status === 'processing') {
            timer = window.setTimeout(poll, 2000)
          }
        })
        .catch((err) => {
          if (cancelled) return
          setPollError((err as { message?: string })?.message ?? '高清切片状态查询失败')
        })
    }
    poll()
    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
  }, [workspaceId, assetId])

  // -------------------------------------------------------------- Viewer
  const ready = derivative?.status === 'ready' && !!derivative.tiles_base_url && !!derivative.width && !!derivative.height

  useEffect(() => {
    if (!ready || !containerRef.current || !derivative) return

    const tilesBaseUrl = resolveSaasApiUrl(derivative.tiles_base_url!)
    const format = derivative.format ?? 'webp'
    const maxLevel = Math.max(0, (derivative.levels ?? 1) - 1)

    const viewer = OpenSeadragon({
      element: containerRef.current,
      showNavigationControl: false,
      showNavigator: true,
      navigatorPosition: 'BOTTOM_RIGHT',
      navigatorSizeRatio: 0.15,
      gestureSettingsMouse: { clickToZoom: false },
      animationTime: 0.4,
      visibilityRatio: 1,
      minZoomImageRatio: 0.9,
      // Custom TileSource (LiangCollection parity, source audit
      // `buildTileSource()`) — a base URL + level/col/row template, never a
      // bare `.dzi` fetch (Vistoera's own tile route doesn't follow
      // OpenSeadragon's default derived-path convention either).
      tileSources: {
        width: derivative.width!,
        height: derivative.height!,
        tileSize: derivative.tile_size ?? 512,
        tileOverlap: derivative.overlap ?? 1,
        minLevel: 0,
        maxLevel,
        getTileUrl: (level: number, x: number, y: number) => `${tilesBaseUrl}${level}/${x}_${y}.${format}`,
      } as unknown as OpenSeadragon.TileSourceOptions,
    })
    viewerRef.current = viewer
    return () => {
      viewer.destroy()
      viewerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, derivative?.tiles_base_url, derivative?.width, derivative?.height, derivative?.levels, derivative?.tile_size, derivative?.overlap, derivative?.format])

  const containerStyle: CSSProperties = {
    position: 'relative',
    width: '100%',
    height,
    background: '#111',
    borderRadius: 6,
    overflow: 'hidden',
  }

  if (pollError) {
    return (
      <div style={containerStyle}>
        <Alert type="error" message={pollError} showIcon style={{ margin: 12 }} />
      </div>
    )
  }

  // status: not_started | pending | processing | failed -> fallback image
  // (never a blank page, spec §33) with a status banner where relevant.
  const status = derivative?.status
  const showFallback = !ready

  return (
    <div style={containerStyle}>
      {showFallback && (
        <>
          {fallbackImageUrl ? (
            <img
              src={fallbackImageUrl}
              alt={alt}
              style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#111' }}
            />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
              暂无图片
            </div>
          )}
          {(status === 'pending' || status === 'processing') && (
            <div style={statusBannerStyle}>
              <LoadingOutlined spin /> 高清细节图正在生成…
            </div>
          )}
          {status === 'failed' && (
            <div style={{ ...statusBannerStyle, background: 'rgba(120,20,20,0.75)' }}>
              高清细节图处理失败{derivative?.error_message ? `：${derivative.error_message}` : ''}
            </div>
          )}
        </>
      )}
      <div ref={containerRef} style={{ position: 'absolute', inset: 0, visibility: ready ? 'visible' : 'hidden' }} />
      {ready && (
        <Space size={4} style={{ position: 'absolute', top: 8, right: 8, zIndex: 5 }}>
          <Tooltip title="放大">
            <Button size="small" icon={<ZoomInOutlined />} onClick={() => viewerRef.current?.viewport.zoomBy(1.4)} />
          </Tooltip>
          <Tooltip title="缩小">
            <Button size="small" icon={<ZoomOutOutlined />} onClick={() => viewerRef.current?.viewport.zoomBy(0.7)} />
          </Tooltip>
          <Tooltip title="重置视图">
            <Button size="small" icon={<ReloadOutlined />} onClick={() => viewerRef.current?.viewport.goHome()} />
          </Tooltip>
          <Tooltip title="全屏">
            <Button size="small" icon={<FullscreenOutlined />} onClick={() => viewerRef.current?.setFullScreen(true)} />
          </Tooltip>
        </Space>
      )}
    </div>
  )
}

const statusBannerStyle: CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  padding: '8px 12px',
  background: 'rgba(0,0,0,0.65)',
  color: '#fff',
  fontSize: 13,
  textAlign: 'center',
}
